import { ArrowLeft, CheckCircle2, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { fixedBidQuote } from '../utils/fixedBidMarkup';

/**
 * The SOW generator's in-app preview (RESTORE_PLAN R3.5, the old full-page
 * generator's step 3): the summary, scope, deliverables, investment, payment
 * terms and schedule, acceptance criteria, assumptions, exclusions and team,
 * as they will read in the PDF.
 */

export interface PreviewableSow {
  title: string;
  clientName: string;
  consultantName?: string;
  description?: string;
  scope: string;
  deliverables: string[];
  startDate?: string;
  endDate?: string;
  pricingModel: 'hourly' | 'fixed' | 'hybrid';
  estimatedHours: number;
  hourlyRate: number;
  fixedPrice?: number;
  /** A fixed bid's markup percent; `fixedPrice` is the base before it, `totalCost` the bid total. */
  markupPercent?: number | '';
  totalCost: number;
  paymentTerms: string;
  paymentDue?: string;
  acceptanceCriteria?: string;
  assumptions: string[];
  exclusions: string[];
  teamMembers: string[];
  requestTypes?: { id: string; name: string }[];
}

const money = (n: number) => `$${(Number(n) || 0).toLocaleString()}`;
const PRICING_LABEL = { hourly: 'Time and materials', fixed: 'Fixed price', hybrid: 'Time and materials with a cap' } as const;

export function SOWPreview({ sow, sizingHours, firmName, leadName, onBack }: {
  sow: PreviewableSow;
  sizingHours?: number;
  firmName?: string;
  leadName?: string;
  onBack: () => void;
}) {
  const hours = sizingHours ?? sow.estimatedHours;
  // A fixed bid's contingency reads as it will in the PDF: base, contingency (x%), then the total.
  const quote = sow.pricingModel === 'fixed' ? fixedBidQuote(sow.fixedPrice, sow.markupPercent) : null;
  return (
    <div className="space-y-6 py-4" data-testid="sow-preview">
      <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="size-4 mr-2" aria-hidden="true" />Back to edit</Button>

      <div className="rounded-lg border p-4 space-y-1">
        <h2>{sow.title}</h2>
        <p className="text-muted-foreground">For {sow.clientName}{(sow.consultantName || firmName) ? ` · by ${sow.consultantName || firmName}` : ''}</p>
        {(sow.startDate || sow.endDate) && <p className="text-muted-foreground">{sow.startDate}{sow.endDate ? ` to ${sow.endDate}` : ''}</p>}
        {leadName && <p className="text-muted-foreground">Engagement lead: {leadName}</p>}
        {sow.description && <p className="pt-2">{sow.description}</p>}
      </div>

      <section>
        <h3 className="mb-2">Scope of Work</h3>
        <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">{sow.scope}</div>
        {sow.requestTypes && sow.requestTypes.length > 0 && (
          <p className="text-muted-foreground mt-2">Request types in scope: {sow.requestTypes.map((r) => r.name).join(', ')}</p>
        )}
      </section>

      <section>
        <h3 className="mb-2">Deliverables ({sow.deliverables.length})</h3>
        <ul className="space-y-1">
          {sow.deliverables.map((d, i) => (
            <li key={`${d}-${i}`} className="flex items-start gap-2"><CheckCircle2 className="size-4 text-green-600 mt-0.5 shrink-0" aria-hidden="true" /><span>{d}</span></li>
          ))}
        </ul>
      </section>

      <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1" data-testid="sow-preview-investment">
        <h3 className="mb-2">Investment</h3>
        <div className="flex justify-between"><span>Pricing model</span><span>{PRICING_LABEL[sow.pricingModel]}</span></div>
        {sow.pricingModel !== 'fixed' && (
          <>
            <div className="flex justify-between"><span>Estimated hours</span><span>{hours} hours</span></div>
            <div className="flex justify-between"><span>Hourly rate</span><span>{money(sow.hourlyRate)}/hr</span></div>
          </>
        )}
        {quote && quote.percent > 0 && quote.base > 0 && (
          <>
            <div className="flex justify-between"><span>Base price</span><span>{money(quote.base)}</span></div>
            <div className="flex justify-between" data-testid="sow-preview-contingency"><span>Contingency ({quote.percent}%)</span><span>{money(quote.markup)}</span></div>
          </>
        )}
        <Separator className="my-2" />
        <div className="flex justify-between"><span>Total</span><span>{money(sow.totalCost)}</span></div>
      </section>

      <section>
        <h3 className="mb-2">Payment Terms</h3>
        {sow.paymentDue && <p>Invoices are due: {sow.paymentDue}</p>}
        {sow.paymentTerms && <p className="text-muted-foreground">{sow.paymentTerms}</p>}
      </section>

      {sow.acceptanceCriteria?.trim() && (
        <section>
          <h3 className="mb-2">Acceptance Criteria</h3>
          <p className="whitespace-pre-wrap">{sow.acceptanceCriteria}</p>
        </section>
      )}

      {(sow.assumptions.length > 0 || sow.exclusions.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {sow.assumptions.length > 0 && (
            <section><h3 className="mb-2">Assumptions</h3><ul className="text-muted-foreground space-y-1">{sow.assumptions.map((a, i) => <li key={i}>• {a}</li>)}</ul></section>
          )}
          {sow.exclusions.length > 0 && (
            <section><h3 className="mb-2">Exclusions</h3><ul className="text-muted-foreground space-y-1">{sow.exclusions.map((e, i) => <li key={i}>• {e}</li>)}</ul></section>
          )}
        </div>
      )}

      {sow.teamMembers.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-2"><Users className="size-4" aria-hidden="true" />Team</h3>
          <p>{sow.teamMembers.join(', ')}</p>
        </section>
      )}
    </div>
  );
}
